import { createElement, Fragment, type ReactNode } from "react";
import type { Vars } from "./types.js";

// Placeholders are `{name}`; anything else in a message is literal text.
const PLACEHOLDER = /\{(\w+)\}/g;

/** Substitutes `{name}` slots with plain values. Unknown slots are left visible, never dropped. */
export function formatTemplate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(PLACEHOLDER, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * Same substitution, but slots may be React nodes (a link, a `<code>`, a list of
 * badges). Keeps whole sentences in the catalog instead of splitting them around
 * markup, which is what makes them translatable.
 */
export function formatNodes(template: string, parts: Readonly<Record<string, ReactNode>>): ReactNode {
  const children: ReactNode[] = [];
  let last = 0;
  for (const match of template.matchAll(PLACEHOLDER)) {
    const name = match[1]!;
    const at = match.index;
    if (at > last) children.push(template.slice(last, at));
    children.push(name in parts ? parts[name] : match[0]);
    last = at + match[0].length;
  }
  if (last < template.length) children.push(template.slice(last));
  // Variadic children, so React indexes them positionally and no keys are needed.
  return createElement(Fragment, null, ...children);
}
